import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Element } from 'react-scroll';

import AgeRatings from 'Components/Utility/Age/AgeRatings';
import BundleCarousel from 'Components/BundleCarousel/BundleCarousel';
import BundleDesktopLayout from 'Features/Bundle/components/BundleRedesign/BundleDesktopLayout';
import BundleEnded from 'Features/Bundle/components/BundleEnded/BundleEnded';
import BundleProductBar from 'Components/BundleProductBar/BundleProductBar';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import ProductOffers from 'Components/ProductOffers/ProductOffers';
import ProductRecommendations from 'Components/ProductRecommendations/ProductRecommendations';

import 'Features/Bundle/components/BundleRedesign/BundlePageRedesign.scss';

const HowToGiftBanner = lazy(() => import(/* webpackChunkName: "HowToGiftBanner" */ 'Components/Gift/HowToGiftBanner'));

const messages = defineMessages({
  rec_header: {
    id: 'bundlepagedesign.recommendations',
    defaultMessage: 'Other products you may like',
  },
});

const BundlePageRedesign = ({
  product,
  siteTheme,
  showGiftBanner,
  preSelectedPnm,
  age: ageRatings,
  switchCarouselGame,
  bundleTiers,
  totalGameCount,
  deliveredGameCount,
  filesTotal,
  downloadTotal,
  pageTotal,
  voucherDelivery,
  mixedBundle,
  validDrm,
  downloadTypes,
  allGames,
  cardSelectedGame,
  endDate,
  isStarDeal,
  displayTypes,
  couponIncluded,
}) => {
  const displayAgeRatings = ageRatings
    ? !['ACB', 'ESRB', 'PEGI', 'USK'].every(
      rating => ageRatings[rating] === 0 || ageRatings[rating] >= 20) : true;

  const {
    franchises,
    desc,
    drm: platforms,
    name,
    type,
    sold_out: soldOut,
    slug,
    display_type: displayType }
    = product;

  const seoDesc = product?.seo.desc;

  const { formatMessage } = useIntl();
  const description = desc === null ? '' : desc;

  const nameTooSmall = (smallName, onSteam) => {
    if (onSteam) {
      return `${smallName} - ${formatMessage(messages.steamgame)}`;
    }

    return `${smallName} - ${formatMessage(messages.game)}`;
  };

  return (
    <di className="BundlePageRedesign bundle-page">
      <BundleProductBar bundle={product} tiers={bundleTiers} />

      {showGiftBanner &&
        <Suspense>
          <div className="mt-4 container">
            <HowToGiftBanner smallBanner giftBannerHeadingTag="div" />
          </div>
        </Suspense>
      }

      <div className="container">
        {soldOut && type === 'bundle' && !window.location.pathname.includes('/preview/') &&
          <BundleEnded
            franchises={franchises}
            slug={slug}
            displayType={displayType}
            darkTheme={siteTheme === 'dark'}
          />
        }
      </div>

      <BundleDesktopLayout
        product={product}
        switchCarouselGame={switchCarouselGame}
        bundleTiers={bundleTiers}
        totalGameCount={totalGameCount}
        deliveredGameCount={deliveredGameCount}
        filesTotal={filesTotal}
        downloadTotal={downloadTotal}
        pageTotal={pageTotal}
        voucherDelivery={voucherDelivery}
        mixedBundle={mixedBundle}
        validDrm={validDrm}
        downloadTypes={downloadTypes}
        preSelectedPnm={preSelectedPnm}
        endDate={endDate}
        isStarDeal={isStarDeal}
        displayTypes={displayTypes}
        couponIncluded={couponIncluded}
      />

      <div className="container">
        <div className="BundlePageRedesign__nameSeoDesc">
          <h1 className="BundlePageRedesign__name product-name">
            {name.length <= 7 && !preSelectedPnm ? nameTooSmall(name, platforms?.steam) : name}
          </h1>
          <div className="BundlePageRedesign__seoDesc">
            <DangerousHTML
              html={seoDesc}
            />
          </div>
        </div>
        {product.template_type !== 'standard_mystery' &&
          product.template_type !== 'tiered_mystery' &&
          <BundleCarousel
            allGames={allGames}
            totalGameCount={totalGameCount}
            product={product}
            selectedSlide={cardSelectedGame}
          />
        }

        {!preSelectedPnm &&
          <ProductOffers product={product} />
        }
        <section className="description-age-container">
          <article className="product-description">
            {product.preorder_info &&
              <h2>
                <FormattedMessage
                  id="bundlepageredesign.about.prepurchase"
                  defaultMessage="Pre-purchase Information"
                />
              </h2>
            }

            {product.preorder_info &&
              <DangerousHTML
                html={product.preorder_info === null ? '' : product.preorder_info}
                className="preorder-bottom-border"
              />
            }

            <Element name="bundle-desc" />

            <h2>
              <FormattedMessage id="bundlepageredesign.about-this" defaultMessage="About this Bundle" tagName="span" />
            </h2>

            <DangerousHTML
              html={description}
            />
          </article>

          <aside className="description-right-column">
            {displayAgeRatings &&
              <div className="bundle-agerating">
                <AgeRatings ratings={ageRatings} />
              </div>
            }
          </aside>
        </section>

        {!preSelectedPnm &&
          <div className="bundle-product-recommendations">
            <ProductRecommendations
              product={product}
              title={formatMessage(messages.rec_header)}
            />
          </div>
        }
      </div>
    </di>
  );
};

BundlePageRedesign.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    cover: PropTypes.string,
    presale: PropTypes.bool,
    type: PropTypes.string,
    currentDiscount: PropTypes.shape({}),
    price: PropTypes.shape({}),
    fullPrice: PropTypes.shape({}),
    hideDiscount: PropTypes.bool,
    bundles: PropTypes.arrayOf(PropTypes.shape({})),
    endDate: PropTypes.string,
    productId: PropTypes.string,
    soldOut: PropTypes.bool,
    regions_included: PropTypes.array,
    regions_excluded: PropTypes.array,
    giveaway: PropTypes.bool,
    platforms: PropTypes.shape({
      windows: PropTypes.bool,
      mac: PropTypes.bool,
      linux: PropTypes.bool,
    }),
    drm: PropTypes.shape({}),
  }),
  siteTheme: PropTypes.string,
  showGiftBanner: PropTypes.bool,
  starDeal: PropTypes.shape({}),
  preSelectedPnm: PropTypes.bool,
  switchCarouselGame: PropTypes.func,
  bundleTiers: PropTypes.arrayOf(PropTypes.shape({})),
  totalGameCount: PropTypes.number,
  deliveredGameCount: PropTypes.number,
  filesTotal: PropTypes.number,
  downloadTotal: PropTypes.number,
  pageTotal: PropTypes.number,
  voucherDelivery: PropTypes.bool,
  mixedBundle: PropTypes.bool,
  validDrm: PropTypes.arrayOf(PropTypes.string),
  downloadTypes: PropTypes.arrayOf(PropTypes.string),
  endDate: PropTypes.string,
  displayTypes: PropTypes.arrayOf(PropTypes.string),
  couponIncluded: PropTypes.bool,
};

BundlePageRedesign.defaultProps = {
  product: {
    _id: '',
    name: '',
    cover: 'default.jpg',
    presale: false,
    type: 'game',
    currentDiscount: {},
    price: {},
    fullPrice: {},
    hideDiscount: false,
    bundles: [],
    endDate: '',
    productId: '',
    soldOut: false,
    regions_included: [],
    regions_excluded: [],
    giveaway: false,
    platforms: {
      windows: false,
      mac: false,
      linux: false,
    },
    drm: {},
    no_release_date: false,
  },
  siteTheme: 'dark',
  showGiftBanner: false,
  starDeal: {},
  preSelectedPnm: false,
  switchCarouselGame: () => { },
  bundleTiers: [],
  totalGameCount: 0,
  deliveredGameCount: 0,
  filesTotal: 0,
  downloadTotal: 0,
  pageTotal: 0,
  voucherDelivery: false,
  mixedBundle: false,
  validDrm: [],
  downloadTypes: [],
  allGames: [],
  cardSelectedGame: 1,
  endDate: '',
  displayTypes: [],
  couponIncluded: false,
};

export default BundlePageRedesign;
